import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check,
  ExternalLink,
  Mail,
  Camera,
  Church,
  Copy,
  Heart,
  ImagePlus,
  X,
} from "lucide-react";
import { SectionHead, formatDate, resolvePhoto, inputCls, textareaCls } from "./shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";

const needStatus = (n) => {
  if (n.status === "completed") return "completed";
  if (n.status === "claimed" || n.claimedBy) return "claimed";
  return "needed";
};

export const NeedsSection = ({ person, needs, onClaim }) => {
  if (!needs?.length) return null;
  const mainNeeds = needs.filter((n) => (n.category || "main") !== "maya");
  const mayaNeeds = needs.filter((n) => n.category === "maya");

  const renderGrid = (list) => (
    <div className="grid sm:grid-cols-2 gap-4">
      {list.map((n) => {
        const status = needStatus(n);
        return (
          <div key={n.id} className="rounded-3xl bg-card shadow-card p-5 flex flex-col transition-gentle hover:shadow-soft">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-serif text-base text-foreground">{n.title}</h3>
            </div>
            {n.details && <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{n.details}</p>}
            {n.link && (
              <a
                href={n.link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Open link <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {person.giftCardEmail && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Mail className="h-3 w-3 text-primary" /> Send digital gift cards to: {person.giftCardEmail}
              </p>
            )}
            <div className="mt-auto pt-3">
              {n.link ? (
                <div>
                  {n.claimCount > 0 && (
                    <p className="mb-2 text-[11px] text-primary" data-testid={`need-sent-${n.id}`}>
                      <Check className="inline h-3 w-3 mr-1" />
                      Sent by {n.claimedBy}
                      {n.claimCount > 1 ? ` and ${n.claimCount - 1} other${n.claimCount > 2 ? "s" : ""}` : ""} — more are always welcome
                    </p>
                  )}
                  <button
                    onClick={() => onClaim(n.id)}
                    data-testid={`need-claim-${n.id}`}
                    className="rounded-full bg-primary-soft text-primary px-4 py-1.5 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-gentle"
                  >
                    I sent this
                  </button>
                </div>
              ) : status === "completed" ? (
                <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-primary" /> Completed by {n.claimedBy}
                </div>
              ) : status === "claimed" ? (
                <div className="text-xs text-primary" data-testid={`need-claimed-${n.id}`}>
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5" /> {n.claimedBy} is on it
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => onClaim(n.id)}
                  data-testid={`need-claim-${n.id}`}
                  className="rounded-full bg-primary-soft text-primary px-4 py-1.5 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-gentle"
                >
                  Claim this
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <section>
      <SectionHead
        label="How you can help"
        title="What we need"
        subtitle="Small things make a big difference. Claim anything that fits your week."
      />
      {mayaNeeds.length > 0 ? (
        <Tabs defaultValue="darius" className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-full bg-secondary/70 p-1 h-auto mb-5">
            <TabsTrigger
              value="darius"
              data-testid="needs-tab-darius"
              className="rounded-full py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              Support his family
            </TabsTrigger>
            <TabsTrigger
              value="maya"
              data-testid="needs-tab-maya"
              className="rounded-full py-2 text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              Back to School
            </TabsTrigger>
          </TabsList>
          <TabsContent value="darius">{renderGrid(mainNeeds)}</TabsContent>
          <TabsContent value="maya">
            <p className="mb-5 text-sm text-muted-foreground">
              Help with back-to-school needs while the family focuses on {person.firstName}'s
              recovery.
            </p>
            {renderGrid(mayaNeeds)}
          </TabsContent>
        </Tabs>
      ) : (
        renderGrid(mainNeeds)
      )}
    </section>
  );
};

export const MessagesSection = ({ person, messages, onPost, posting }) => {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    const ok = await onPost({ name: name.trim(), body: body.trim(), file });
    if (ok) {
      setName("");
      setBody("");
      setFile(null);
    }
  };

  return (
    <section>
      <SectionHead label="Send love" title={`Messages for ${person.firstName}`} />
      <form onSubmit={submit} className="rounded-3xl bg-card shadow-card p-5 sm:p-6 space-y-3 mb-6">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          data-testid="message-name-input"
          className={inputCls}
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={`Leave a note for ${person.firstName} and the family\u2026`}
          data-testid="message-body-input"
          className={textareaCls}
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary cursor-pointer transition-gentle">
            <ImagePlus className="h-4 w-4" />
            {file ? (
              <span className="inline-flex items-center gap-1.5 text-primary">
                {file.name}
                <button type="button" onClick={(e) => { e.preventDefault(); setFile(null); }} className="hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ) : (
              "Add photo"
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="message-photo-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          <button
            type="submit"
            disabled={posting}
            data-testid="message-submit"
            className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle disabled:opacity-60"
          >
            {posting ? "Sending\u2026" : `Send to ${person.firstName}`}
          </button>
        </div>
      </form>
      <div className="space-y-4" data-testid="message-list">
        {messages.map((m) => (
          <div key={m.id} className="rounded-3xl bg-card/70 backdrop-blur shadow-card p-5">
            {m.photoUrl && (
              <img src={resolvePhoto(m.photoUrl)} alt="" className="mb-3 rounded-2xl max-h-72 w-auto object-cover" loading="lazy" />
            )}
            <p className="font-serif italic text-base text-foreground/85 leading-relaxed">"{m.body}"</p>
            <div className="mt-2 flex items-baseline justify-between gap-3 flex-wrap">
              <div className="text-xs text-primary">— {m.name}</div>
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{formatDate(m.date)}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const QuestionsSection = ({ person, onPost, posting }) => {
  const [form, setForm] = useState({ name: "", contactMethod: "either", contact: "", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    const ok = await onPost(form);
    if (ok) setForm({ name: "", contactMethod: "either", contact: "", message: "" });
  };
  const methods = [
    { value: "email", label: "Email" },
    { value: "call", label: "Call/Text" },
    { value: "either", label: "Either" },
  ];
  return (
    <section>
      <SectionHead
        label="Reach out"
        title="Questions for the family"
        subtitle="Have a question, want to drop off a meal, or just want to talk? Leave a note and the best way to reach you. Someone will get back to you when there's a quiet moment."
      />
      <form onSubmit={submit} className="rounded-3xl bg-card shadow-card p-5 sm:p-6 space-y-3">
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Your name"
          data-testid="question-name"
          className={inputCls}
        />
        <div>
          <div className="text-xs text-muted-foreground mb-2">Best way to reach you</div>
          <div className="flex gap-2">
            {methods.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setForm({ ...form, contactMethod: m.value })}
                data-testid={`question-method-${m.value}`}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-gentle ${
                  form.contactMethod === m.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-primary-soft text-primary hover:bg-primary/20"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <input
          value={form.contact}
          onChange={(e) => setForm({ ...form, contact: e.target.value })}
          placeholder="Email or phone"
          data-testid="question-contact"
          className={inputCls}
        />
        <textarea
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Your question or message"
          data-testid="question-message"
          className={textareaCls}
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-muted-foreground">
            Your contact info is private — only {person.firstName}'s family can see it.
          </p>
          <button
            type="submit"
            disabled={posting}
            data-testid="question-submit"
            className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle disabled:opacity-60"
          >
            {posting ? "Sending\u2026" : "Send"}
          </button>
        </div>
      </form>
    </section>
  );
};

export const ChurchSection = ({ person, churchMessages, onPost, posting }) => {
  const church = person.church;
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  if (!church?.name) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    const ok = await onPost({ name: name.trim(), body: body.trim() });
    if (ok) {
      setName("");
      setBody("");
    }
  };

  return (
    <section>
      <SectionHead label="Faith family" title={church.name} subtitle={church.description} />
      <div className="rounded-3xl bg-gradient-to-br from-card to-primary-soft/30 shadow-card p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary-soft shrink-0">
            <Church className="h-5 w-5 text-primary" />
          </span>
          <div>
            <div className="font-serif text-lg text-foreground">{church.name}</div>
            <p className="text-xs text-muted-foreground">Watch services live or browse the church website.</p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          {church.siteUrl && (
            <a
              href={church.siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="church-site-link"
              className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle"
            >
              Open church site
            </a>
          )}
          {church.streamUrl && (
            <a
              href={church.streamUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="church-stream-link"
              className="rounded-full bg-primary-soft text-primary px-5 py-2 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-gentle"
            >
              Watch live stream
            </a>
          )}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">
          If the stream doesn't load here, tap "Open church site" above.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="text-xs uppercase tracking-[0.2em] text-primary">
            Leave a message for {person.firstName}'s church family
          </div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            data-testid="church-message-name"
            className={inputCls}
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="A prayer, encouragement, or word for the church family\u2026"
            data-testid="church-message-body"
            className={textareaCls}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={posting}
              data-testid="church-message-submit"
              className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle disabled:opacity-60"
            >
              {posting ? "Posting\u2026" : "Post message"}
            </button>
          </div>
        </form>
        {churchMessages?.length > 0 && (
          <div className="mt-5 space-y-3">
            {churchMessages.map((m) => (
              <div key={m.id} className="rounded-2xl bg-card/80 p-4">
                <p className="font-serif italic text-sm text-foreground/85">"{m.body}"</p>
                <div className="mt-1.5 text-xs text-primary">— {m.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const PageFooter = ({ slug, toast }) => {
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
      toast({ title: "Link copied", description: "Share it with people you'd like to include." });
    } catch (e) {
      toast({ title: "Couldn't copy", description: "Please copy the address from your browser bar." });
    }
  };
  return (
    <>
      <section className="rounded-3xl bg-card shadow-card p-6 sm:p-8 text-center">
        <Heart className="h-5 w-5 text-primary fill-primary mx-auto" />
        <h2 className="mt-3 font-serif text-xl text-foreground">Going through something similar?</h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
          OpenHART helps you create a private page like this one — so you don't have to explain
          everything to everyone you love. View plans, sign up, or search for a loved one on the main site.
        </p>
        <a
          href="https://www.openhart.us"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-hart-cta"
          className="mt-5 inline-flex items-center justify-center rounded-full bg-primary-soft text-primary px-6 py-2.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-gentle"
        >
          Visit openhart.us
        </a>
      </section>
      <footer className="text-center space-y-4 pt-2">
        <p className="font-serif italic text-sm text-muted-foreground">So you don't have to explain everything.</p>
        <p className="text-xs text-muted-foreground">
          This page is private. Share the link only with people you'd like to include.
        </p>
        <button
          onClick={copyLink}
          data-testid="copy-share-link"
          className="inline-flex items-center gap-2 rounded-full bg-card shadow-card px-5 py-2 text-xs font-medium text-primary hover:shadow-soft transition-gentle"
        >
          <Copy className="h-3.5 w-3.5" /> Copy share link
        </button>
        <div className="pt-2 text-[11px] uppercase tracking-[0.25em] text-primary">OpenHART</div>
        <a
          href="https://www.openhart.us"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-hart-site-link"
          className="inline-block text-xs text-primary hover:underline underline-offset-4"
        >
          www.openhart.us
        </a>
        <p className="text-[11px] text-muted-foreground max-w-md mx-auto">
          © 2026 OpenHART. Private page shared with permission of the family. Please do not
          redistribute the link, passcode, or screenshots.
        </p>
        <Link
          to={`/${slug}/admin`}
          data-testid="family-admin-link"
          className="inline-block text-xs text-muted-foreground hover:text-primary transition-gentle underline underline-offset-4 pb-6"
        >
          Admin sign in
        </Link>
      </footer>
    </>
  );
};
